import { AfterViewInit, Component, ElementRef, HostListener, OnInit, QueryList, ViewChildren } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/auth/auth.service';
import { FeedbackType } from 'src/app/general-components/feedback/feedback-type';
import { FeedbackSettings } from 'src/app/general-components/feedback/feedback-settings';
import { ProductImportReportService } from '../product-import-report.service';
import { ImportAreaStatus, ImportDisplayStatus } from '../product-import-report-types';
import {
  reportStatusBadgeClass,
  reportStatusBorderClass,
  reportStatusLabel,
  toDisplayStatus,
} from '../product-import-report-status.util';

export type MoveDirection = 'left' | 'right' | 'up' | 'down';

@Component({
  selector: 'app-product-import-status',
  templateUrl: './product-import-status.component.html',
  styleUrls: ['./product-import-status.component.scss'],
})
export class ProductImportStatusComponent implements OnInit, AfterViewInit {
  @ViewChildren('tile') tileElements?: QueryList<ElementRef<HTMLElement>>;

  areas: ImportAreaStatus[] = [];
  isLoading: boolean = false;

  editMode: boolean = false;
  isSaving: boolean = false;
  /** Columns currently shown by the responsive grid; measured from the DOM. */
  columns: number = 1;

  hasProductFactoryAccess: boolean = false;
  cognitoGroups: string[] = [];

  feedbackSettings: FeedbackSettings = {
    identifier: '',
    show: false,
    type: FeedbackType.error,
    message: 'Er is iets misgegaan sorry',
    closeButton: false,
  };

  private orderSnapshot: string[] = [];
  private readonly productFactoryGroupName = 'AWS-PF';

  constructor(
    private service: ProductImportReportService,
    private authService: AuthService,
    private router: Router
  ) {}

  @HostListener('window:resize')
  onResize(): void {
    this.refreshColumns();
  }

  ngOnInit(): void {
    this.cognitoGroups = this.authService.getCognitoGroups();
    this.hasProductFactoryAccess = this.cognitoGroups.includes(this.productFactoryGroupName);

    if (this.hasProductFactoryAccess) {
      this.loadBoard();
    }
  }

  ngAfterViewInit(): void {
    this.refreshColumns();
    this.tileElements?.changes.subscribe(() => {
      this.refreshColumns();
    });
  }

  enterEditMode(): void {
    this.orderSnapshot = this.areas.map(area => area.code);
    this.editMode = true;
    this.refreshColumns();
  }

  cancelEdit(): void {
    if (this.isDirty() && !this.confirmDiscard()) {
      return;
    }
    this.restoreSnapshot();
    this.editMode = false;
  }

  save(): void {
    if (!this.isDirty()) {
      this.editMode = false;
      return;
    }
    this.isSaving = true;
    this.service.reorderAreas(this.areas.map(area => area.code)).subscribe({
      next: () => {
        this.isSaving = false;
        this.editMode = false;
        this.loadBoard();
      },
      error: (error: Error) => {
        this.isSaving = false;
        this.updateFeedback(FeedbackType.error, 'Fout opgetreden bij het opslaan van de volgorde');
        console.error(error);
      },
    });
  }

  isDirty(): boolean {
    const current = this.areas.map(area => area.code);
    return (
      current.length !== this.orderSnapshot.length || current.some((code, index) => code !== this.orderSnapshot[index])
    );
  }

  drop(event: CdkDragDrop<ImportAreaStatus[]>): void {
    moveItemInArray(this.areas, event.previousIndex, event.currentIndex);
  }

  moveTile(index: number, direction: MoveDirection): void {
    const target = this.targetIndex(index, direction, this.columns);
    if (target !== index) {
      moveItemInArray(this.areas, index, target);
    }
  }

  /** Clamped destination index for a move; pure so it can be unit-tested. */
  targetIndex(index: number, direction: MoveDirection, columns: number): number {
    const offsets: Record<MoveDirection, number> = {
      left: -1,
      right: 1,
      up: -columns,
      down: columns,
    };
    const target = index + offsets[direction];
    return Math.min(Math.max(target, 0), this.areas.length - 1);
  }

  canMove(index: number, direction: MoveDirection): boolean {
    return this.targetIndex(index, direction, this.columns) !== index;
  }

  loadBoard(): void {
    this.isLoading = true;
    this.service.getAreaBoard().subscribe({
      next: (areas: ImportAreaStatus[]) => {
        this.areas = areas;
        this.isLoading = false;
      },
      error: (error: Error) => {
        this.updateFeedback(
          FeedbackType.error,
          'Fout opgetreden bij het ophalen van de importstatus',
          false,
          'Terug naar hoofdmenu'
        );
        this.isLoading = false;
        console.error(error);
      },
    });
  }

  updateFeedback(
    type: FeedbackType,
    message: string,
    closeButton: boolean = true,
    buttonText: string | undefined = undefined
  ): void {
    this.feedbackSettings.identifier = '';
    this.feedbackSettings.show = true;
    this.feedbackSettings.closeButton = closeButton;
    this.feedbackSettings.buttonText = buttonText;
    this.feedbackSettings.type = type;
    this.feedbackSettings.message = message;
  }

  statusLabel(area: ImportAreaStatus): string {
    return reportStatusLabel(this.displayStatus(area));
  }

  statusBadgeClass(area: ImportAreaStatus): string {
    return reportStatusBadgeClass(this.displayStatus(area));
  }

  tileBorderClass(area: ImportAreaStatus): string {
    return reportStatusBorderClass(this.displayStatus(area));
  }

  // Folds the latest status and its counts into the UI display state (orange when the
  // run succeeded with warnings); shared by the label, badge and border helpers.
  private displayStatus(area: ImportAreaStatus): ImportDisplayStatus {
    return toDisplayStatus(area.latestStatus, area.counts);
  }

  returnHome(): void {
    void this.router.navigate(['home']);
  }

  private restoreSnapshot(): void {
    const byCode = new Map(this.areas.map(area => [area.code, area]));
    this.areas = this.orderSnapshot
      .map(code => byCode.get(code))
      .filter((area): area is ImportAreaStatus => area != null);
  }

  private confirmDiscard(): boolean {
    return window.confirm('Niet-opgeslagen wijzigingen worden verwijderd. Doorgaan?');
  }

  private refreshColumns(): void {
    const tiles = this.tileElements?.toArray() ?? [];
    if (tiles.length === 0) {
      this.columns = 1;
      return;
    }
    const firstTop = tiles[0].nativeElement.offsetTop;
    let columns = 0;
    for (const tile of tiles) {
      if (tile.nativeElement.offsetTop !== firstTop) {
        break;
      }
      columns++;
    }
    this.columns = Math.max(columns, 1);
  }
}
